import React, { useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'
import IconButton from 'srcReact/components/common2/IconButton'
import NotFound from 'srcReact/components/common2/NotFound'
import ReactMenu from 'srcReact/components/common2/ReactMenu'
import ReactTable from 'srcReact/components/common2/ReactTable'
import Icon from 'srcReact/components/icons/Icon'
import ErrorFallback from 'srcReact/ErrorPages/ErrorFallback'
import { formatLastUpdateDate, getDateFormat3 } from 'srcReact/helpers/datetimeHelper'
import { downloadMedia } from 'srcReact/helpers/fileHelper'
import { getDescryptedMedia, getIconNameFromExtension, getNativeDecryptedMedia } from 'srcReact/helpers/utils'
import { organizationSettingsActions } from 'srcReact/OrganizationSettingsPage/actions'
import styles from './styles.module.scss'

function DocumentList({ orgId, showActions, handleUpdate, handleDelete }) {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const {
    common: { isMobile, isNative },
    organizationSettings: {
      orgFiles: { list, stats, loadingGetFile, getFileError }
    }
  } = useSelector((state) => state)

  useEffect(() => {
    if (orgId && orgId > 0) {
      dispatch(organizationSettingsActions.getOrgFiles(orgId, 0, appConstantSettings.PAGINATION_QUANTITY))
    }
  }, [orgId])

  const options = (row) => {
    // let isLoggedInUser = row.user_id === loggedInUser.id
    let actions = []
    actions.push({
      text: t('global.update'),
      onClick: () => handleUpdate(row)
    })
    actions.push({
      text: t('global.delete'),
      onClick: () => handleDelete(row)
    })
    actions.push({
      text: t('global.download'),
      onClick: () => handleDownload(row)
    })

    return actions
  }

  const handleDownload = (file) => {
    if (file?.file_info) {
      const { url, is_original, original_tag, mime_type } = file.file_info
      const isOriginal = is_original == 0 ? 0 : 1
      getDescryptedMedia(url, isOriginal, original_tag, mime_type).then((data) => {
        // native decryption
        if (isNative) {
          getNativeDecryptedMedia(url, data, isOriginal, original_tag, mime_type, file.name)
          return
        }
        downloadMedia(isOriginal, data, file.file_info.name)
      })
    }
  }

  const columns = useMemo(() => {
    let cols = [
      {
        Header: '',
        id: 'icon',
        accessor: (row, idx) => {
          if (row?.file_url?.length > 0) {
            return <Icon name='link' size='large24' />
          } else if (row?.file_info?.name) {
            const extention = row.file_info.name.split('.').pop().toLowerCase()
            const iconName = getIconNameFromExtension(extention || '')
            return iconName ? <Icon name={iconName} size='large24' /> : null
          } else return null
        },
        className: styles.rowIcon
      },
      {
        Header: t('global.documentName'),
        id: 'name',
        accessor: (row, idx) => {
          if (row?.file_url?.length > 0) {
            return (
              <a className={styles.fileName} href={row.file_url} target='_blank'>
                {row.file_url}
              </a>
            )
          } else if (row?.file_info?.name) {
            return (
              <button className={styles.fileName} onClick={() => handleDownload(row)}>
                {row.file_info.name}
              </button>
            )
          } else return null
        },
        className: styles.rowFileName
      },
      {
        Header: t('global.lastUpdated'),
        id: 'date',
        accessor: (row, idx) => (
          <div className={styles.lastUpdated}>{formatLastUpdateDate(row?.updatedAt, getDateFormat3())}</div>
        ),
        className: styles.rowLastUpdated
      }
    ]
    if (showActions) {
      cols.push({
        Header: '',
        id: 'options',
        accessor: (row, idx) => (
          <ReactMenu options={options(row)} menuButtonCSS={styles.menuButtonCSS} menuCSS={styles.menuCSS} />
        )
      })
    } else {
      cols.push({
        Header: '',
        id: 'options',
        accessor: (row, idx) => {
          if (row?.file_info?.name) {
            return (
              <IconButton
                className={styles.downloadBtnIcon}
                iconName='download24Coloured'
                iconSize='large24'
                onClick={() => handleDownload(row)}
              />
            )
          }
          return null
        }
      })
    }
    return cols
  }, [])

  const handleCurrentPage = (page) => {
    let offset = (page - 1) * appConstantSettings.PAGINATION_QUANTITY
    dispatch(organizationSettingsActions.getOrgFiles(orgId, offset, appConstantSettings.PAGINATION_QUANTITY))
  }

  return (
    <React.Fragment>
      {getFileError ? (
        getFileError === appConstantSettings.HTTP500 ? (
          <ErrorFallback isServerError={true} className={styles.errorFallback} />
        ) : (
          <ErrorMessage text={getFileError} className={styles.error} />
        )
      ) : null}

      {list?.length > 0 && (
        <ReactTable
          columns={columns}
          data={list}
          loading={loadingGetFile}
          className={styles.tableClassName}
          handleCurrentPage={handleCurrentPage}
          pageSize={appConstantSettings.PAGINATION_QUANTITY}
          pagination={{ total: stats.total, offset: stats.next_offset }}
        />
      )}
      {!loadingGetFile && list?.length === 0 && <NotFound title={t('global.no_results')} />}
    </React.Fragment>
  )
}

export default DocumentList
